import { useState, useEffect } from 'react'
import { getQuestions } from '../services/survey-question-api'
import type { SurveyQuestion } from '../types/survey.types'

interface UseSurveyQuestionsState {
  questions: SurveyQuestion[]
  loading: boolean
  error: Error | null
  refresh: () => void
}

export function useSurveyQuestions(surveyId: string | null): UseSurveyQuestionsState {
  const [questions, setQuestions] = useState<SurveyQuestion[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    if (!surveyId) {
      setQuestions([])
      return
    }

    let cancelled = false

    const fetchQuestions = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await getQuestions(surveyId)
        if (!cancelled) {
          setQuestions(data)
          setLoading(false)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Failed to fetch questions'))
          setLoading(false)
        }
      }
    }

    fetchQuestions()

    return () => {
      cancelled = true
    }
  }, [surveyId, refreshKey])

  const refresh = () => {
    setRefreshKey(prev => prev + 1)
  }

  return { questions, loading, error, refresh }
}
